import React, { useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, logout } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { apiUrl } from "../../utils/apiUrl";
import { STAGE_IDS } from "../../data/unhealthyGameData";
import Swal from "sweetalert2";

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData } = useSelector(
    (state) => state.theme
  );

  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const alreadyPlayedHandledRef = useRef(false);
  const isFromMobileApp = searchParams.get("fromMobileApp") === "true";

  useEffect(() => {
    const otp = searchParams.get("otp");
    const demo = searchParams.get("demo");
    // Avoid stale hydrated user triggering "already played" before OTP/demo login finishes
    if (otp || demo) {
      dispatch(logout());
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  useEffect(() => {
    const body = document.body;
    const prevImage = body.style.backgroundImage;
    const prevSize = body.style.backgroundSize;
    const prevPosition = body.style.backgroundPosition;
    const prevRepeat = body.style.backgroundRepeat;
    const prevAttachment = body.style.backgroundAttachment;

    body.style.backgroundImage = `url(${process.env.PUBLIC_URL || ""}/unhealthy-game/img/login_bg.jpg)`;
    body.style.backgroundSize = "cover";
    body.style.backgroundPosition = "center center";
    body.style.backgroundRepeat = "no-repeat";
    body.style.backgroundAttachment = "fixed";

    return () => {
      body.style.backgroundImage = prevImage;
      body.style.backgroundSize = prevSize;
      body.style.backgroundPosition = prevPosition;
      body.style.backgroundRepeat = prevRepeat;
      body.style.backgroundAttachment = prevAttachment;
    };
  }, []);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    console.log("user",user);
    if (status === "succeeded" && user.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }

  }, [status, user]);

  

 useEffect(() => {
  if (status !== "failed") return;
  const msg = error ? String(error) : "Session expired";
  const redirect = user && user.backButtonRedirect ? user.backButtonRedirect : "/";
  Swal.fire("Session Expired!", msg, "error").then(() => {
    window.location.href = redirect;
  });
}, [status, error, user]);

  // "Already played" only when the full slide run is finished (spotGameStatus.completed).
  // Do not use userPlayedCount alone — partial progress must allow resume.
  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user) return;
    if (alreadyPlayedHandledRef.current) return;

    const redirect =
      user.backButtonRedirect || process.env.REACT_APP_BASE_URL || "/";

    const showOnceAndRedirect = async () => {
      if (alreadyPlayedHandledRef.current) return;
      alreadyPlayedHandledRef.current = true;
      await Swal.fire({
        title: "You have already played!",
        icon: "error",
        timer: 3000,
        timerProgressBar: true,
        allowOutsideClick: false,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
      if (isFromMobileApp) {
        window.GameStatus?.postMessage?.("navigate_back");
      } else {
        window.location.href = redirect;
      }
    };

    if (!adminToken) return;

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json();
        if (cancelled || alreadyPlayedHandledRef.current) return;
        const spotAlreadyPlayed = Boolean(data?.completed || data?.finalized);
        if (data.success && spotAlreadyPlayed) {
          await showOnceAndRedirect();
          return;
        }

        // Unhealthy-flow check: treat user as already played when all stages are marked played.
        const unhealthyResp = await fetch(
          `${apiUrl("/unhealthy-game/state")}?${new URLSearchParams({
            userId: String(user.userId || ""),
            sessionId: String(user.sessionId || ""),
            organizationId: String(user.organizationId || ""),
          }).toString()}`,
          {
            headers: {
              Authorization: `Bearer ${adminToken}`,
            },
          }
        );
        const unhealthyData = await unhealthyResp.json().catch(() => ({}));
        if (cancelled || alreadyPlayedHandledRef.current) return;
        const stages = unhealthyData?.data?.stages || {};
        const unhealthyAlreadyPlayed =
          unhealthyData?.success &&
          STAGE_IDS.length > 0 &&
          STAGE_IDS.every((id) => stages[id] === "played");
        if (unhealthyAlreadyPlayed) {
          await showOnceAndRedirect();
        }
      } catch (e) {
        console.error("spotGameStatus check on login:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, user, adminToken, isAdmin]);

  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user) return;

    const redirectTimer = setTimeout(() => {
      if (!alreadyPlayedHandledRef.current) navigate("/intro");
    }, 500000);

    return () => {
      clearTimeout(redirectTimer);
    };
  }, [status, user, isAdmin, navigate]);

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      <div className="container-fluid">
        <div className="row">
          <div className="col-sm-5 col-md-5 col-lg-5 col-xs-12 auto login-logo-wrap">
            <img src="/unhealthy-game/img/logo.gif" className="login-logo show-intro" alt="Escape Game Logo" />
          </div>
        </div>
        <div className="login-cta-wrap">
          {user && status === "succeeded" && (
            <Link to="/intro" className="btn btn-info begin-play-btn">
              Begin Play
            </Link>
          )}
        </div>
        <div className="full-screen">Please rotate your device for the best experience</div>
      </div>
    </div>
  );
}

export default Login;
